import React, { useEffect, useMemo, useState } from 'react'
import {
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
    Typography,
    Box,
    Button,
    CircularProgress,
    LinearProgress,
    Popover,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Checkbox,
} from '@material-ui/core'
import {
    GetApp as DownloadIcon,
    MoreVert as MoreIcon,
    Visibility as VisibilityIcon,
    ArrowUpward as ArrowUpIcon,
    ArrowDownward as ArrowDownIcon,
    Schedule as ScheduleIcon,
    CompareArrows as CompareArrowsIcon,
} from '@material-ui/icons'
import FileDownload from 'js-file-download'
import { format } from 'date-fns'
import {
    AuditSnapshotRow,
    MetricDefinition,
    computeMetricDeltaPct,
    formatMetricValue,
} from './auditMetrics'
import { HealthStatusCell } from './HealthStatusCell'

type CompareMode = 'off' | 'selecting' | 'focused'

const formatReportDate = (createdAt: string): string => {
    try {
        return format(new Date(createdAt), 'MMM d, yyyy')
    } catch {
        return createdAt
    }
}

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
    },
    compareBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
        marginBottom: theme.spacing(1.5),
        minHeight: 36,
    },
    compareBarLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    compareHint: {
        fontSize: '0.8rem',
        color: theme.palette.text.secondary,
    },
    compareButton: {
        textTransform: 'none',
        whiteSpace: 'nowrap',
    },
    tableWrapper: {
        width: '100%',
        overflow: 'auto',
        maxHeight: 'calc(100vh - 280px)',
        position: 'relative',
    },
    table: {
        minWidth: 960,
        tableLayout: 'fixed',
        width: '100%',
    },
    stickyCol: {
        position: 'sticky',
        left: 0,
        zIndex: 3,
        backgroundColor: theme.palette.background.paper,
        width: 200,
        minWidth: 200,
        maxWidth: 220,
        borderRight: `1px solid ${theme.palette.divider}`,
    },
    stickyHead: {
        position: 'sticky',
        left: 0,
        zIndex: 4,
        backgroundColor: theme.palette.background.paper,
        width: 200,
        minWidth: 200,
        maxWidth: 220,
        borderRight: `1px solid ${theme.palette.divider}`,
        verticalAlign: 'bottom',
    },
    stickyActionCol: {
        position: 'sticky',
        left: 0,
        zIndex: 3,
        backgroundColor: theme.palette.grey[50],
        width: 200,
        minWidth: 200,
        maxWidth: 220,
        borderRight: `1px solid ${theme.palette.divider}`,
        verticalAlign: 'middle',
    },
    metricHead: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        hyphens: 'auto',
        width: 96,
        minWidth: 88,
        maxWidth: 110,
        paddingTop: theme.spacing(1.5),
        paddingBottom: theme.spacing(1.5),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        fontWeight: 600,
        fontSize: '0.7rem',
        lineHeight: 1.25,
        verticalAlign: 'bottom',
        backgroundColor: theme.palette.background.paper,
    },
    metricHeadAlt: {
        backgroundColor: theme.palette.grey[100],
    },
    healthStatusHead: {
        width: 170,
        minWidth: 161,
        maxWidth: 184,
    },
    metricCell: {
        whiteSpace: 'nowrap',
        verticalAlign: 'top',
        width: 96,
        minWidth: 88,
        maxWidth: 110,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
    },
    metricCellAlt: {
        backgroundColor: theme.palette.grey[100],
    },
    healthStatusCell: {
        width: 170,
        minWidth: 161,
        maxWidth: 184,
        whiteSpace: 'normal',
    },
    actionMetricCell: {
        backgroundColor: theme.palette.grey[50],
        borderBottom: `1px solid ${theme.palette.divider}`,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
    actionMetricCellAlt: {
        backgroundColor: theme.palette.grey[100],
    },
    actionCellContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(0.75),
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
    },
    requestButton: {
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        textTransform: 'none',
        whiteSpace: 'nowrap',
        fontSize: '0.8rem',
        padding: theme.spacing(0.5, 1.25),
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    requestButtonContent: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
    },
    nextWeekly: {
        fontSize: '0.7rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.3,
    },
    progressRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
        width: '100%',
    },
    progressBar: {
        width: '100%',
        height: 4,
        borderRadius: 2,
    },
    progressLabel: {
        fontSize: '0.65rem',
        color: theme.palette.text.secondary,
    },
    value: {
        fontWeight: 500,
        fontSize: '0.95rem',
    },
    deltaUp: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        color: theme.palette.success.main,
        fontSize: '0.7rem',
        marginTop: 2,
    },
    deltaDown: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        color: theme.palette.error.main,
        fontSize: '0.7rem',
        marginTop: 2,
    },
    emptyState: {
        padding: theme.spacing(4),
        textAlign: 'center',
    },
    emptyWithAction: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(2),
        padding: theme.spacing(4),
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.6)',
        zIndex: 5,
    },
    rowLabel: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
    },
    reportIdentity: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        minWidth: 0,
    },
    selectCheckbox: {
        padding: 0,
        marginRight: theme.spacing(0.25),
    },
    weeklyIcon: {
        color: theme.palette.text.secondary,
        fontSize: 16,
        flexShrink: 0,
    },
    reportDateButton: {
        padding: 0,
        minWidth: 0,
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.875rem',
        color: theme.palette.primary.main,
        justifyContent: 'flex-start',
        '&:hover': {
            backgroundColor: 'transparent',
            textDecoration: 'underline',
        },
    },
    popover: {
        minWidth: 200,
    },
    listItem: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
}))

interface Props {
    rows: AuditSnapshotRow[]
    metrics: MetricDefinition[]
    isLoading?: boolean
    isRefreshing?: boolean
    emptyMessage?: string
    onView?: (reportUrl: string) => void
    generateLabel?: string
    onGenerate?: () => void
    isGenerating?: boolean
    showGenerateProgress?: boolean
    generateProgressPercent?: number
    generateProgressLabel?: string
    nextWeeklyLabel?: string
    /** When set, report dates are clickable to open snapshot detail. */
    onOpenDetail?: (row: AuditSnapshotRow) => void
}

export const SnapshotComparisonGrid: React.FC<Props> = ({
    rows,
    metrics,
    isLoading = false,
    isRefreshing = false,
    emptyMessage = 'No snapshots found',
    onView,
    generateLabel = 'Generate Report',
    onGenerate,
    isGenerating = false,
    showGenerateProgress = false,
    generateProgressPercent = 0,
    generateProgressLabel = '',
    nextWeeklyLabel,
    onOpenDetail,
}) => {
    const classes = useStyles()
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
    const [selectedRow, setSelectedRow] = useState<AuditSnapshotRow | null>(
        null,
    )
    const [localDownloading, setLocalDownloading] = useState(false)
    const [compareMode, setCompareMode] = useState<CompareMode>('off')
    const [selectedIds, setSelectedIds] = useState<(string | number)[]>([])

    const rowIdsKey = useMemo(
        () => rows.map((row) => String(row.id)).join(','),
        [rows],
    )

    // Drop stale selections when the underlying row set changes
    useEffect(() => {
        const validIds = new Set(rows.map((row) => row.id))
        setSelectedIds((prev) => prev.filter((id) => validIds.has(id)))
    }, [rowIdsKey, rows])

    // Exit focused compare if fewer than 2 selected reports remain
    useEffect(() => {
        if (compareMode === 'focused' && selectedIds.length < 2) {
            setCompareMode('off')
            setSelectedIds([])
        }
    }, [compareMode, selectedIds.length])

    const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds])

    const displayRows = useMemo(() => {
        if (compareMode !== 'focused') {
            return rows
        }
        return rows.filter((row) => selectedIdSet.has(row.id))
    }, [compareMode, rows, selectedIdSet])

    // Rows are newest-first; compare to the next older visible row
    const getPreviousRow = (index: number): AuditSnapshotRow | null => {
        return index < displayRows.length - 1 ? displayRows[index + 1] : null
    }

    const handleStartCustomCompare = () => {
        setSelectedIds([])
        setCompareMode('selecting')
    }

    const handleCancelCustomCompare = () => {
        setSelectedIds([])
        setCompareMode('off')
    }

    const handleApplyCustomCompare = () => {
        if (selectedIds.length < 2) {
            return
        }
        setCompareMode('focused')
    }

    const handleShowAll = () => {
        setSelectedIds([])
        setCompareMode('off')
    }

    const handleToggleRow = (rowId: string | number) => {
        setSelectedIds((prev) => {
            if (prev.includes(rowId)) {
                return prev.filter((id) => id !== rowId)
            }
            return [...prev, rowId]
        })
    }

    const handleMenuClick = (
        event: React.MouseEvent<HTMLButtonElement>,
        row: AuditSnapshotRow,
    ) => {
        setAnchorEl(event.currentTarget)
        setSelectedRow(row)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
    }

    const handleDownload = () => {
        if (selectedRow?.xlsx_url) {
            setLocalDownloading(true)
            fetch(selectedRow.xlsx_url)
                .then((response) => response.blob())
                .then((blob) => {
                    FileDownload(
                        blob,
                        `${formatReportDate(selectedRow.created_at)}.xlsx`,
                    )
                    setLocalDownloading(false)
                })
                .catch(() => {
                    setLocalDownloading(false)
                })
            handleMenuClose()
        }
    }

    const handleView = () => {
        if (selectedRow?.xlsx_url && onView) {
            onView(selectedRow.xlsx_url)
            handleMenuClose()
        }
    }

    const renderGenerateControls = () => (
        <Box className={classes.actionCellContent}>
            {onGenerate && (
                <Button
                    variant="contained"
                    className={classes.requestButton}
                    onClick={onGenerate}
                    disabled={isGenerating}
                    size="small"
                >
                    <span className={classes.requestButtonContent}>
                        {isGenerating && (
                            <CircularProgress size={14} color="inherit" />
                        )}
                        {isGenerating ? 'Generating...' : generateLabel}
                    </span>
                </Button>
            )}
            {showGenerateProgress && (
                <Box className={classes.progressRow}>
                    <LinearProgress
                        variant={
                            generateProgressPercent > 0
                                ? 'determinate'
                                : 'indeterminate'
                        }
                        value={generateProgressPercent}
                        className={classes.progressBar}
                    />
                    {generateProgressLabel && (
                        <Typography className={classes.progressLabel}>
                            {generateProgressLabel}
                        </Typography>
                    )}
                </Box>
            )}
            {nextWeeklyLabel && !isGenerating && (
                <Typography className={classes.nextWeekly}>
                    Next weekly report: {nextWeeklyLabel}
                </Typography>
            )}
        </Box>
    )

    const renderCompareBar = () => {
        if (compareMode === 'off') {
            return (
                <Box className={classes.compareBar}>
                    <Box className={classes.compareBarLeft}>
                        <Button
                            variant="outlined"
                            size="small"
                            className={classes.compareButton}
                            onClick={handleStartCustomCompare}
                            disabled={rows.length < 2}
                            startIcon={<CompareArrowsIcon />}
                        >
                            Custom Compare
                        </Button>
                    </Box>
                </Box>
            )
        }

        if (compareMode === 'selecting') {
            const count = selectedIds.length
            return (
                <Box className={classes.compareBar}>
                    <Box className={classes.compareBarLeft}>
                        <Typography className={classes.compareHint}>
                            {count === 0
                                ? 'Select reports to compare'
                                : `${count} selected`}
                            {count > 0 && count < 2 ? ' — pick at least 2' : ''}
                        </Typography>
                        <Button
                            size="small"
                            className={classes.compareButton}
                            onClick={handleCancelCustomCompare}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            className={classes.compareButton}
                            onClick={handleApplyCustomCompare}
                            disabled={count < 2}
                        >
                            Compare
                        </Button>
                    </Box>
                </Box>
            )
        }

        return (
            <Box className={classes.compareBar}>
                <Box className={classes.compareBarLeft}>
                    <Typography className={classes.compareHint}>
                        Comparing {displayRows.length} reports
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        className={classes.compareButton}
                        onClick={handleShowAll}
                    >
                        Show all
                    </Button>
                </Box>
            </Box>
        )
    }

    if (isLoading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="200px"
            >
                <CircularProgress />
            </Box>
        )
    }

    if (rows.length === 0) {
        return (
            <Paper className={classes.emptyWithAction} elevation={0}>
                {renderGenerateControls()}
                <Typography variant="body1" color="textSecondary">
                    {emptyMessage}
                </Typography>
            </Paper>
        )
    }

    return (
        <Box className={classes.root}>
            {renderCompareBar()}
            <TableContainer
                component={Paper}
                className={classes.tableWrapper}
                elevation={0}
            >
                {isRefreshing && (
                    <Box className={classes.loadingOverlay}>
                        <CircularProgress size={40} />
                    </Box>
                )}
                <Table className={classes.table} size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell className={classes.stickyHead}>
                                Report
                            </TableCell>
                            {metrics.map((metric, metricIndex) => (
                                <TableCell
                                    key={metric.key}
                                    className={`${classes.metricHead}${
                                        metricIndex % 2 === 1
                                            ? ` ${classes.metricHeadAlt}`
                                            : ''
                                    }${
                                        metric.format === 'health_status'
                                            ? ` ${classes.healthStatusHead}`
                                            : ''
                                    }`}
                                    align="right"
                                >
                                    {metric.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell className={classes.stickyActionCol}>
                                {renderGenerateControls()}
                            </TableCell>
                            {metrics.map((metric, metricIndex) => (
                                <TableCell
                                    key={`action-${metric.key}`}
                                    className={`${classes.actionMetricCell}${
                                        metricIndex % 2 === 1
                                            ? ` ${classes.actionMetricCellAlt}`
                                            : ''
                                    }`}
                                />
                            ))}
                        </TableRow>
                        {displayRows.map((row, rowIndex) => {
                            const previous = getPreviousRow(rowIndex)
                            const isSelected = selectedIdSet.has(row.id)
                            return (
                                <TableRow key={row.id} hover>
                                    <TableCell className={classes.stickyCol}>
                                        <Box className={classes.rowLabel}>
                                            <Box
                                                className={
                                                    classes.reportIdentity
                                                }
                                            >
                                                {compareMode ===
                                                    'selecting' && (
                                                    <Checkbox
                                                        className={
                                                            classes.selectCheckbox
                                                        }
                                                        size="small"
                                                        color="primary"
                                                        checked={isSelected}
                                                        onChange={() =>
                                                            handleToggleRow(
                                                                row.id,
                                                            )
                                                        }
                                                        inputProps={{
                                                            'aria-label': `Select report ${formatReportDate(
                                                                row.created_at,
                                                            )}`,
                                                        }}
                                                    />
                                                )}
                                                {row.is_scheduled && (
                                                    <Tooltip title="Weekly report">
                                                        <ScheduleIcon
                                                            className={
                                                                classes.weeklyIcon
                                                            }
                                                        />
                                                    </Tooltip>
                                                )}
                                                <Typography variant="body2">
                                                    {onOpenDetail ? (
                                                        <Button
                                                            className={
                                                                classes.reportDateButton
                                                            }
                                                            onClick={() =>
                                                                onOpenDetail(
                                                                    row,
                                                                )
                                                            }
                                                        >
                                                            {formatReportDate(
                                                                row.created_at,
                                                            )}
                                                        </Button>
                                                    ) : (
                                                        formatReportDate(
                                                            row.created_at,
                                                        )
                                                    )}
                                                </Typography>
                                            </Box>
                                            <Tooltip
                                                title={
                                                    row.xlsx_url
                                                        ? 'Options'
                                                        : 'No spreadsheet available. Filter to a property to download its workbook.'
                                                }
                                            >
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) =>
                                                            handleMenuClick(
                                                                e,
                                                                row,
                                                            )
                                                        }
                                                        disabled={
                                                            !row.xlsx_url ||
                                                            localDownloading
                                                        }
                                                    >
                                                        <MoreIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                    {metrics.map((metric, metricIndex) => {
                                        if (metric.format === 'health_status') {
                                            return (
                                                <TableCell
                                                    key={metric.key}
                                                    className={`${
                                                        classes.metricCell
                                                    } ${
                                                        classes.healthStatusCell
                                                    }${
                                                        metricIndex % 2 === 1
                                                            ? ` ${classes.metricCellAlt}`
                                                            : ''
                                                    }`}
                                                    align="right"
                                                >
                                                    <HealthStatusCell
                                                        metrics={row.metrics}
                                                        previousMetrics={
                                                            previous?.metrics ??
                                                            null
                                                        }
                                                    />
                                                </TableCell>
                                            )
                                        }
                                        const value = row.metrics[metric.key]
                                        const prevValue = previous
                                            ? previous.metrics[metric.key]
                                            : null
                                        const delta = previous
                                            ? computeMetricDeltaPct(
                                                  value,
                                                  prevValue,
                                              )
                                            : null
                                        return (
                                            <TableCell
                                                key={metric.key}
                                                className={`${
                                                    classes.metricCell
                                                }${
                                                    metricIndex % 2 === 1
                                                        ? ` ${classes.metricCellAlt}`
                                                        : ''
                                                }`}
                                                align="right"
                                            >
                                                <Typography
                                                    className={classes.value}
                                                    component="div"
                                                >
                                                    {formatMetricValue(
                                                        value,
                                                        metric.format,
                                                    )}
                                                </Typography>
                                                {delta !== null &&
                                                    Math.abs(delta) >= 0.05 && (
                                                        <Box
                                                            className={
                                                                delta > 0
                                                                    ? classes.deltaUp
                                                                    : classes.deltaDown
                                                            }
                                                        >
                                                            {delta > 0 ? (
                                                                <ArrowUpIcon
                                                                    style={{
                                                                        fontSize: 12,
                                                                    }}
                                                                />
                                                            ) : (
                                                                <ArrowDownIcon
                                                                    style={{
                                                                        fontSize: 12,
                                                                    }}
                                                                />
                                                            )}
                                                            {delta > 0
                                                                ? '+'
                                                                : ''}
                                                            {delta.toFixed(1)}%
                                                        </Box>
                                                    )}
                                            </TableCell>
                                        )
                                    })}
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                className={classes.popover}
            >
                <List>
                    <ListItem
                        onClick={handleDownload}
                        className={classes.listItem}
                        disabled={localDownloading || !selectedRow?.xlsx_url}
                    >
                        <ListItemIcon>
                            <DownloadIcon />
                        </ListItemIcon>
                        <ListItemText primary="Download" />
                    </ListItem>
                    <ListItem
                        onClick={handleView}
                        className={classes.listItem}
                        disabled={!onView || !selectedRow?.xlsx_url}
                    >
                        <ListItemIcon>
                            <VisibilityIcon />
                        </ListItemIcon>
                        <ListItemText primary="View" />
                    </ListItem>
                </List>
            </Popover>
        </Box>
    )
}
